import { useEffect, useCallback } from 'react';

// Inject tracking scripts and fire booking events.
// Base scripts for Meta, TikTok, and GA4 are loaded by GTM (GTM-M3MF7VKX).
// This hook is responsible for: Google Ads gtag injection + all booking event firing.
export function useTrackingPixels(config) {
  // Google Analytics — inject only if gaId is set and GTM hasn't already loaded gtag
  useEffect(() => {
    const gaId = config?.gaId;
    if (!gaId || document.getElementById('ga-script')) return;
    const s1 = document.createElement('script');
    s1.id = 'ga-script';
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(s1);
    const s2 = document.createElement('script');
    s2.id = 'ga-config';
    s2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
    document.head.appendChild(s2);
  }, [config?.gaId]);

  // Google Ads — inject conversion tracking tag
  useEffect(() => {
    const adsId = config?.googleAdsConversionId;
    if (!adsId || document.getElementById('gads-config')) return;
    // Reuse gtag base if GA or GTM already loaded it; otherwise inject it
    if (!document.getElementById('ga-script') && !document.getElementById('gads-base')) {
      const s1 = document.createElement('script');
      s1.id = 'gads-base';
      s1.async = true;
      s1.src = `https://www.googletagmanager.com/gtag/js?id=${adsId}`;
      document.head.appendChild(s1);
      const s2 = document.createElement('script');
      s2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());`;
      document.head.appendChild(s2);
    }
    const s = document.createElement('script');
    s.id = 'gads-config';
    s.textContent = `gtag('config','${adsId}');`;
    document.head.appendChild(s);
  }, [config?.googleAdsConversionId]);

  const trackEvent = useCallback((eventName, data = {}) => {
    // Google Analytics 4
    if (window.gtag) {
      if (eventName === 'purchase') {
        window.gtag('event', 'purchase', { value: data.value, currency: 'USD', transaction_id: data.ref });
      } else if (eventName === 'begin_checkout') {
        window.gtag('event', 'begin_checkout', { value: data.value, currency: 'USD' });
      } else if (eventName === 'add_to_cart') {
        window.gtag('event', 'add_to_cart', { value: data.value, currency: 'USD' });
      }
    }

    // Google Ads conversion — fires only on confirmed booking
    if (config?.googleAdsConversionId && config?.googleAdsLabel && window.gtag) {
      if (eventName === 'purchase') {
        window.gtag('event', 'conversion', {
          send_to: `${config.googleAdsConversionId}/${config.googleAdsLabel}`,
          value: data.value,
          currency: 'USD',
          transaction_id: data.ref,
        });
      }
    }

    // Meta Pixel — base script loaded by GTM
    if (config?.metaPixelId && window.fbq) {
      if (eventName === 'purchase') {
        // Purchase and Schedule must be separate triggers (client requirement)
        window.fbq('track', 'Purchase', { value: data.value, currency: 'USD' });
      } else if (eventName === 'begin_checkout') {
        window.fbq('track', 'InitiateCheckout');
      } else if (eventName === 'add_to_cart') {
        // Date/time selection maps to Schedule, not AddToCart
        window.fbq('track', 'Schedule');
      } else if (eventName === 'view_booking_page') {
        window.fbq('track', 'InitiateCheckout');
      }
    }

    // TikTok Pixel — base script loaded by GTM
    if (config?.tiktokPixelId && window.ttq) {
      if (eventName === 'purchase') {
        window.ttq.track('PlaceAnOrder', { value: data.value, currency: 'USD' });
      } else if (eventName === 'begin_checkout') {
        window.ttq.track('InitiateCheckout');
      } else if (eventName === 'view_booking_page') {
        window.ttq.track('InitiateCheckout');
      }
    }
  }, [config?.gaId, config?.metaPixelId, config?.tiktokPixelId, config?.googleAdsConversionId, config?.googleAdsLabel]);

  return { trackEvent };
}
